import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  HelpCircle,
  Package,
  ShoppingCart,
  Wallet,
  Users,
  TrendingUp,
  Building2,
  Search,
  ChevronRight,
  ExternalLink,
  BookOpen,
  GitBranch
} from 'lucide-react';
import { MODULE_DOCS } from '../data/moduleDocs';
import FlowDiagram from '../components/FlowDiagram';

const ICON_MAP = {
  Package,
  ShoppingCart,
  Wallet,
  Users,
  TrendingUp,
  Building2,
  GitBranch
};

/**
 * Help page with module documentation.
 * Users can browse documentation by module and navigate to each section.
 */
export default function HelpPage() {
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [selectedSubId, setSelectedSubId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredDocs = useMemo(() => {
    if (!searchTerm.trim()) return MODULE_DOCS;
    const term = searchTerm.toLowerCase();
    const stepMatches = (steps) =>
      steps?.some((st) => {
        const lbl = typeof st === 'string' ? st : st?.label;
        return lbl?.toLowerCase().includes(term);
      });
    return MODULE_DOCS.map((group) => {
      const filteredModules = group.modules.filter(
        (m) =>
          m.title.toLowerCase().includes(term) ||
          m.sections.some(
            (s) =>
              s.title.toLowerCase().includes(term) ||
              (s.content && s.content.toLowerCase().includes(term)) ||
              stepMatches(s.flow?.steps)
          )
      );
      if (filteredModules.length === 0) return null;
      return { ...group, modules: filteredModules };
    }).filter(Boolean);
  }, [searchTerm]);

  const selectedGroup = MODULE_DOCS.find((g) => g.id === selectedModuleId);
  const selectedSub = selectedGroup?.modules.find((m) => m.id === selectedSubId);

  const handleSelectModule = (groupId, subId) => {
    setSelectedModuleId(groupId);
    setSelectedSubId(subId ?? null);
  };

  return (
    <div className="help-page">
      <div className="help-header">
        <div className="help-header-content">
          <div className="help-header-icon">
            <HelpCircle size={32} aria-hidden />
          </div>
          <div>
            <h1 className="help-title">Centro de Ayuda</h1>
            <p className="help-subtitle">
              Documentación de los módulos del ERP Minimarket
            </p>
          </div>
        </div>

        <div className="help-search">
          <Search size={20} className="help-search-icon" aria-hidden />
          <input
            type="search"
            placeholder="Buscar en la documentación..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="help-search-input"
            aria-label="Buscar documentación"
          />
        </div>
      </div>

      <div className="help-body">
        <aside className="help-sidebar">
          <nav className="help-nav" aria-label="Navegación de ayuda">
            {filteredDocs.map((group) => {
              const Icon = ICON_MAP[group.icon] ?? Package;
              return (
                <div key={group.id} className="help-nav-group">
                  <div className="help-nav-group-title">
                    <Icon size={18} aria-hidden />
                    <span>{group.title}</span>
                  </div>
                  <ul className="help-nav-list">
                    {group.modules.map((mod) => (
                      <li key={mod.id}>
                        <button
                          type="button"
                          className={`help-nav-item ${
                            selectedModuleId === group.id &&
                            selectedSubId === mod.id
                              ? 'active'
                              : ''
                          }`}
                          onClick={() => handleSelectModule(group.id, mod.id)}
                        >
                          <span>{mod.title}</span>
                          <ChevronRight size={16} aria-hidden />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </nav>
        </aside>

        <main className="help-content">
          {selectedSub ? (
            <article className="help-article">
              <div className="help-article-header">
                <h2 className="help-article-title">{selectedSub.title}</h2>
                <Link
                  to={selectedSub.path}
                  className="help-article-link"
                  title={`Ir a ${selectedSub.title}`}
                >
                  <ExternalLink size={18} aria-hidden />
                  Ir al módulo
                </Link>
              </div>

              <div className="help-article-sections">
                {selectedSub.sections.map((section, idx) => (
                  <section key={idx} className="help-section">
                    <h3 className="help-section-title">{section.title}</h3>
                    {section.content && (
                      <p className="help-section-content">{section.content}</p>
                    )}
                    {section.flow && (
                      <FlowDiagram
                        steps={section.flow.steps}
                        layout={section.flow.layout || 'vertical'}
                      />
                    )}
                  </section>
                ))}
              </div>
            </article>
          ) : (
            <div className="help-empty">
              <div className="help-empty-icon">
                <BookOpen size={48} aria-hidden />
              </div>
              <h2 className="help-empty-title">Selecciona un módulo</h2>
              <p className="help-empty-text">
                Elige un módulo en el menú de la izquierda para ver su
                documentación.
              </p>
              {searchTerm && filteredDocs.length === 0 && (
                <p className="help-empty-hint">
                  No se encontraron resultados para &quot;{searchTerm}&quot;
                </p>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
